import { Box, Grid, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import DropdownButton from 'new-components/buttons/dropdown-button'
/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** API */
import ClinicApi from 'api/master/clinic.api'
import ReportApi, { FindAllReportInterface } from 'api/report.api'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import { useDownloadExcel } from 'component/Excel/hooks/useExcel'
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import MainButton from 'new-components/buttons/main-button'

/** REPORT */
import IncomeList, { SummaryList, SummaryPayment, SummaryPD, SummaryTx, SummaryTxPD } from './IncomeList'

/** CONSTANTS */
import { routeName } from 'routes/routes-name'
import { getBranch, numberFormat, toBuddhistYear } from 'utils/app.utils'
import { dateTimeToView, dateToView } from 'utils/date.utils'

/** STYLE */
import ProductTypesApi from 'api/setting/default-products/product-type.api'
import ProductsApi from 'api/warehouses/product.api'
import OperativeTypeApi from 'api/setting/treatments/operative-type.api'
import OperativeApi from 'api/setting/treatments/operative.api'
import * as UseStyled from 'features/report/useStyled'
import SelectInput from 'new-components/inputs/select-input'
import SelectCheckBoxInput from 'new-components/inputs/select_checkbox_input'

export default function IncomeOperativeReport() {
  const { t } = useTranslation()
  const history = useHistory()
  const componentRef = useRef<HTMLDivElement>(null)
  const momentNowRange = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]

  const [loading, setLoading] = useState<boolean>(true)
  const [branches, setBranches] = useState<any[]>([])


  const [filter, setFilter] = useState<{ loading: boolean, branchId: any, page: number, pageLimit: number, sortBy: string, sortType: string, rangeDate: string[], productTypeIds: number[], productIds: number[], operativeTypeIds: number[], operativeIds: number[] }>({ loading: true, branchId: getBranch(), page: 1, pageLimit: 10, sortBy: 'createAt', sortType: 'ASC', rangeDate: [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')], productTypeIds: [], productIds: [], operativeTypeIds: [], operativeIds: [] });


  const [incomeData, setIncomeData] = useState<any[]>([])
  const [incomeSummeryData, setIncomeSummeryData] = useState<any[]>([])
  const [incomeDataProduct, setIncomeDataProduct] = useState<any[]>([])
  const [incomeDataTX, setIncomeDataTX] = useState<any[]>([])
  const [incomeExport, setIncomeExportData] = useState<any[]>([])
  const [incomeRowCount, setIncomeRowCount] = useState<number>(0)


  const [productType, setProductType] = useState<any[]>([])
  const [productTypeHasMore, setProductTypeHasMore] = useState(true)
  const [productTypeLoading, setProductTypeLoading] = useState(false)
  const productTypePage = useRef(1)
  const productTypeSearch = useRef('')

  const [product, setProduct] = useState<any[]>([])
  const [productHasMore, setProductHasMore] = useState(true)
  const [productLoading, setProductLoading] = useState(false)
  const productPage = useRef(1)
  const productSearch = useRef('')

  const [operativeTypeList, setOperativeTypeList] = useState<any[]>([])
  const [operativeTypeHasMore, setOperativeTypeHasMore] = useState(true)
  const [operativeTypeLoading, setOperativeTypeLoading] = useState(false)
  const operativeTypePage = useRef(1)
  const operativeTypeSearch = useRef('')

  const [operativeList, setOperativeList] = useState<any[]>([])
  const [operativeHasMore, setOperativeHasMore] = useState(true)
  const [operativeLoading, setOperativeLoading] = useState(false)
  const operativePage = useRef(1)
  const operativeSearch = useRef('')

  const verifyPermission = async () => {
    const condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 200
    }
    ReportApi.reportList(condition).then(({ data }) => {
      const check = _.find(data, (val: any) => String(val.reportKey) === 'INCOME_SIMPLE_DAILY_OPERATIVE') ? true : false
      if (!check) {
        history.push(routeName.report)
        return false
      }
    })

    /** MASTER DATA */
    ClinicApi.findAllBranches()
      .then(({ data }) => setBranches(data))
      .catch((e) => {
        return
      })
    return true
  }

  useEffect(() => {
    verifyPermission()
  }, [])

  /** TABLE 1 */
  const fetchIncome = useCallback(() => {
    let condition: FindAllReportInterface = {
      page: filter.page,
      pageLimit: filter.pageLimit,
      branchId: filter.branchId,
    }
    if (filter.sortType) condition = { ...condition, sortType: filter.sortType }
    if (filter.sortBy) condition = { ...condition, sortBy: filter.sortBy }

    if (filter.productTypeIds.length > 0) condition = { ...condition, productTypeId: filter.productTypeIds.join(',') }
    if (filter.productIds.length > 0) condition = { ...condition, productId: filter.productIds.join(',') }
    if (filter.operativeTypeIds.length > 0) condition = { ...condition, operativeTypeId: filter.operativeTypeIds.join(',') }
    if (filter.operativeIds.length > 0) condition = { ...condition, operativeId: filter.operativeIds.join(',') }

    if (filter.rangeDate[0] !== null && filter.rangeDate[1] !== null) condition = { ...condition, dateStart: filter.rangeDate[0], dateEnd: filter.rangeDate[1] }

    if (filter.loading) ReportApi.reportIncomeSimpleDailyOperative(condition)
      .then(({ headers, data }) => {
        setIncomeData(data)
        setIncomeRowCount(headers['x-total'])
      })
      .catch((e) => setLoading(false))
      .finally(() => setLoading(false))
  }, [filter.loading])

  /** TABLE PRODUCT */

  const fetchIncomeProduct = useCallback(() => {
    let condition: FindAllReportInterface = {
      page: filter.page,
      pageLimit: filter.pageLimit,
      branchId: filter.branchId,
    }
    if (filter.sortType) condition = { ...condition, sortType: filter.sortType }
    if (filter.sortBy) condition = { ...condition, sortBy: filter.sortBy }

    if (filter.productTypeIds.length > 0) condition = { ...condition, productTypeId: filter.productTypeIds.join(',') }
    if (filter.productIds.length > 0) condition = { ...condition, productId: filter.productIds.join(',') }
    if (filter.operativeTypeIds.length > 0) condition = { ...condition, operativeTypeId: filter.operativeTypeIds.join(',') }
    if (filter.operativeIds.length > 0) condition = { ...condition, operativeId: filter.operativeIds.join(',') }

    if (filter.rangeDate[0] !== null && filter.rangeDate[1] !== null) condition = { ...condition, dateStart: filter.rangeDate[0], dateEnd: filter.rangeDate[1] }

    if (filter.loading) ReportApi.reportIncomeSimpleDailySummery(condition)
      .then(({ headers, data }) => {
        const d = _.groupBy(data, 'refItemType')
        setIncomeDataProduct(d['ORDER_PRODUCT'])
        setIncomeDataTX(d['ORDER_TREATMENT'])
        setIncomeSummeryData(data)
      })
      .catch((e) => setLoading(false))
      .finally(() => setLoading(false))
  }, [filter.loading])


  const onChangeIncomeSort = (sortByVal: string, sortTypeVal: any) => {
    setFilter((val) => ({ ...val, sortType: sortTypeVal, sortBy: sortByVal, loading: true }))
  }

  const onChangeIncomeRowPerPage = (limit: number) => {
    setFilter((val) => ({ ...val, pageLimit: limit, loading: true }))

  }

  const onChangeIncomePage = (page: number) => {
    setFilter((val) => ({ ...val, page: page, loading: true }))
  }

  const loadIncomeExport = useCallback(async () => {
    let condition: FindAllReportInterface = {
      page: 1,
      pageLimit: 1000,
      branchId: filter.branchId,
      export: 1
    }

    if (filter.rangeDate[0] !== null && filter.rangeDate[1] !== null) condition = { ...condition, dateStart: filter.rangeDate[0], dateEnd: filter.rangeDate[1] }
    if (filter.productTypeIds.length > 0) condition = { ...condition, productTypeId: filter.productTypeIds.join(',') }
    if (filter.productIds.length > 0) condition = { ...condition, productId: filter.productIds.join(',') }
    if (filter.operativeTypeIds.length > 0) condition = { ...condition, operativeTypeId: filter.operativeTypeIds.join(',') }
    if (filter.operativeIds.length > 0) condition = { ...condition, operativeId: filter.operativeIds.join(',') }

    if (filter.loading) ReportApi.reportIncomeSimpleDailyOperative(condition)
      .then(({ data }) => setIncomeExportData(data))
      .catch((e) => setFilter((val) => ({ ...val, loading: false })))
      .finally(() => setFilter((val) => ({ ...val, loading: false })))
  }, [filter.loading])

  const loadProductType = useCallback((search = '', page = 1) => {
    setProductTypeLoading(true)
    productTypeSearch.current = search
    productTypePage.current = page
    ProductTypesApi.findAll({ status: 'ACTIVE', pageLimit: 50, page, search })
      .then(({ data }) => {
        if (page === 1) setProductType(data)
        else setProductType((prev) => [...prev, ...data])
        setProductTypeHasMore(data.length >= 50)
      })
      .catch(() => { return })
      .finally(() => setProductTypeLoading(false))
  }, [])

  const loadMoreProductType = useCallback(() => {
    loadProductType(productTypeSearch.current, productTypePage.current + 1)
  }, [loadProductType])

  const loadProduct = useCallback((search = '', page = 1) => {
    setProductLoading(true)
    productSearch.current = search
    productPage.current = page
    const params: any = { status: 'ACTIVE', pageLimit: 50, page, search }
    if (filter.productTypeIds.length > 0) params.itemTypeId = filter.productTypeIds.join(',')
    ProductsApi.findByType(params)
      .then(({ data }) => {
        if (page === 1) setProduct(data)
        else setProduct((prev) => [...prev, ...data])
        setProductHasMore(data.length >= 50)
      })
      .catch(() => { return })
      .finally(() => setProductLoading(false))
  }, [filter.productTypeIds])

  const loadMoreProduct = useCallback(() => {
    loadProduct(productSearch.current, productPage.current + 1)
  }, [loadProduct])

  useEffect(() => {
    loadProductType()
  }, [loadProductType])

  useEffect(() => {
    loadProduct()
  }, [loadProduct])

  const loadOperativeType = useCallback((search = '', page = 1) => {
    setOperativeTypeLoading(true)
    operativeTypeSearch.current = search
    operativeTypePage.current = page
    OperativeTypeApi.findAll({ status: 'ACTIVE', pageLimit: 50, page, search })
      .then(({ data }) => {
        if (page === 1) setOperativeTypeList(data)
        else setOperativeTypeList((prev) => [...prev, ...data])
        setOperativeTypeHasMore(data.length >= 50)
      })
      .catch(() => { return })
      .finally(() => setOperativeTypeLoading(false))
  }, [])

  const loadMoreOperativeType = useCallback(() => {
    loadOperativeType(operativeTypeSearch.current, operativeTypePage.current + 1)
  }, [loadOperativeType])

  const loadOperative = useCallback((search = '', page = 1) => {
    setOperativeLoading(true)
    operativeSearch.current = search
    operativePage.current = page
    const params: any = { status: 'ACTIVE', pageLimit: 50, page, search }
    if (filter.operativeTypeIds.length > 0) params.operativeTypeId = filter.operativeTypeIds.join(',')
    OperativeApi.findAll(params)
      .then(({ data }) => {
        if (page === 1) setOperativeList(data)
        else setOperativeList((prev) => [...prev, ...data])
        setOperativeHasMore(data.length >= 50)
      })
      .catch(() => { return })
      .finally(() => setOperativeLoading(false))
  }, [filter.operativeTypeIds])

  const loadMoreOperative = useCallback(() => {
    loadOperative(operativeSearch.current, operativePage.current + 1)
  }, [loadOperative])


  useEffect(() => {
    loadOperativeType()
  }, [loadOperativeType])

  useEffect(() => {
    loadOperative()
  }, [loadOperative])

  /** UseEffect */
  useEffect(() => {
    fetchIncome()
  }, [fetchIncome])

  useEffect(() => {
    fetchIncomeProduct()
  }, [fetchIncomeProduct])


  useEffect(() => {
    loadIncomeExport()
  }, [loadIncomeExport,])

  const { onDownload } = useDownloadExcel({
    tableExportRef: componentRef.current,
    filename: `${t(`REPORT.REPORT_NAME.INCOME_SIMPLE_DAILY_OPERATIVE`)} ${filter.rangeDate ? `${filter.rangeDate[0]} - ${filter.rangeDate[1]}` : ''}`,
    sheetName: `${t(`REPORT.REPORT_NAME.INCOME_SIMPLE_DAILY_OPERATIVE`)}`
  })

  const branchName = _.get(_.find(branches, { branchId: filter.branchId }), 'branchName') || _.get(_.find(branches, { branchId: filter.branchId }), 'branchNameEn') || '-'
  const productTypeNames = filter.productTypeIds.length > 0 ? productType.filter((o: any) => filter.productTypeIds.includes(o.productTypeId)).map((o: any) => o.productTypeName).join(', ') : t('REPORT.FILTER.ALL')
  const productNames = filter.productIds.length > 0 ? product.filter((o: any) => filter.productIds.includes(o.productId)).map((o: any) => o.productName).join(', ') : t('REPORT.FILTER.ALL')
  const operativeTypeNames = filter.operativeTypeIds.length > 0 ? operativeTypeList.filter((o: any) => filter.operativeTypeIds.includes(o.operativeTypeId)).map((o: any) => o.operativeTypeName).join(', ') : t('REPORT.FILTER.ALL')
  const operativeNames = filter.operativeIds.length > 0 ? operativeList.filter((o: any) => filter.operativeIds.includes(o.operativeId)).map((o: any) => o.operativeName).join(', ') : t('REPORT.FILTER.ALL')

  const onLoadData = () => {
    fetchIncome()
    fetchIncomeProduct()
    loadIncomeExport()
  }

  return (
    <Box sx={{ height: '100%' }}>
      <HeaderCard text={t(`REPORT.REPORT_NAME.INCOME_SIMPLE_DAILY_OPERATIVE`)} />
      <Box sx={{ px: 3, pt: 2 }}>
        <Grid container spacing={2} >
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              label={t('REPORT.FILTER.BRANCH')}
              value={filter.branchId}
              onChange={(e) => setFilter((val) => ({ ...val, branchId: e.target.value, page: 1, pageLimit: 10 }))}
              options={[
                ...branches.map(b => ({ value: b.branchId, name: b.branchName || b.branchNameEn }))
              ]}
              width="100%"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <InputSecondNewRangePicker
              inputHeight={42}
              value={filter.rangeDate || emptyRangePicker}
              onchange={(val: any) => {
                if (_.isEmpty(val)) setFilter((val) => ({ ...val, rangeDate: emptyRangePicker, page: 1, }))
                else setFilter((old) => ({ ...old, rangeDate: [moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')], page: 1, }))
              }}
              label={''}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectCheckBoxInput
              label={t('ประเภทผลิตภัณฑ์')}
              options={productType.map((o: any) => ({ id: o.productTypeId, name: o.productTypeName }))}
              selected={filter.productTypeIds}
              onChange={(selected) => {
                setFilter((val) => ({ ...val, productTypeIds: selected.filter((v: any) => v !== null), productIds: [] }))
              }}
              searchable
              searchPlaceholder="ค้นหาประเภทผลิตภัณฑ์..."
              onSearch={(search) => loadProductType(search)}
              onOpen={() => loadProductType()}
              onLoadMore={loadMoreProductType}
              hasMore={productTypeHasMore}
              loading={productTypeLoading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectCheckBoxInput
              label={t('รายการผลิตภัณฑ์')}
              options={product.map((o: any) => ({ id: o.productId, name: o.productName }))}
              selected={filter.productIds}
              onChange={(selected) => {
                setFilter((val) => ({ ...val, productIds: selected.filter((v: any) => v !== null) }))
              }}
              searchable
              searchPlaceholder="ค้นหารายการผลิตภัณฑ์..."
              onSearch={(search) => loadProduct(search)}
              onOpen={() => loadProduct()}
              onLoadMore={loadMoreProduct}
              hasMore={productHasMore}
              loading={productLoading}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectCheckBoxInput
              label={t('ประเภทหัตถการ')}
              options={operativeTypeList.map((o: any) => ({ id: o.operativeTypeId, name: o.operativeTypeName }))}
              selected={filter.operativeTypeIds}
              onChange={(selected) => {
                setFilter((val) => ({ ...val, operativeTypeIds: selected.filter((v: any) => v !== null), operativeIds: [] }))
              }}
              searchable
              searchPlaceholder="ค้นหาประเภทหัตถการ..."
              onSearch={(search) => loadOperativeType(search)}
              onOpen={() => loadOperativeType()}
              onLoadMore={loadMoreOperativeType}
              hasMore={operativeTypeHasMore}
              loading={operativeTypeLoading}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectCheckBoxInput
              label={t('หัตถการ')}
              options={operativeList.map((o: any) => ({ id: o.operativeId, name: o.operativeName }))}
              selected={filter.operativeIds}
              onChange={(selected) => {
                setFilter((val) => ({ ...val, operativeIds: selected.filter((v: any) => v !== null) }))
              }}
              searchable
              searchPlaceholder="ค้นหาหัตถการ..."
              onSearch={(search) => loadOperative(search)}
              onOpen={() => loadOperative()}
              onLoadMore={loadMoreOperative}
              hasMore={operativeHasMore}
              loading={operativeLoading}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={1} xl={1}>
            <Box maxWidth={'100px'} width={'100%'}  >
              <MainButton
                title={t('INPUT.SEARCH')}
                onClick={() => {
                  setFilter((val) => ({ ...val, page: 1, loading: true }))
                }}
                type={'primary'}
                variant={'contained'}
                style={{ width: '120px' }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={11} xl={3} display={'flex'} justifyContent={'flex-end'}>
            <DropdownButton
              title={t('PRINT_DOCUMENT')}
              onChange={(e) => {
                if (e === 'Excel') onDownload()
                else if (e === 'PDF') {
                  const style = document.createElement('style')
                  style.innerHTML = `
                      @media print {
                        @page { size: A3 landscape; margin: 6mm }
                      }
                    `
                  document.head.appendChild(style)
                  window.print()
                  document.head.removeChild(style)
                }
              }}
              options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]}
            />
          </Grid>
        </Grid>

        <Box className={'mt-3 pl-xl-3 pr-xl-2 pb-4'}>
          <IncomeList
            page={filter.page}
            pageLimit={filter.pageLimit}
            rowCount={incomeRowCount}
            sortBy={filter.sortBy}
            sortType={filter.sortType}
            data={incomeData}
            dataExport={incomeExport}
            onRequestSort={onChangeIncomeSort}
            handleChangeRowsPerPage={onChangeIncomeRowPerPage}
            handleChangePage={onChangeIncomePage}
          />
        </Box>
        <Grid container gridColumn={2} spacing={2}>
          <Grid item >
            <SummaryPayment data={incomeExport} />
          </Grid>
          <Grid item >
            <SummaryList data={incomeExport} />
          </Grid>
          <Grid item >
            <SummaryTx data={incomeExport} />
          </Grid>
          <Grid item >
            <SummaryPD data={incomeDataProduct} />
          </Grid>
          <Grid item >
            <SummaryTxPD operative={incomeExport} product={incomeDataProduct} />
          </Grid>
        </Grid>
      </Box>
      <Loading show={filter.loading} type="fullLoading" />

      <Box id="print-table-container" ref={componentRef}>
        <Typography fontSize="14px" fontWeight={500}>{t(`REPORT.REPORT_NAME.INCOME_SIMPLE_DAILY_OPERATIVE`)}</Typography>
        <Box display="flex" flexDirection="row" alignItems="center" gap="12px" flexWrap="wrap" mb={1}>
          <Typography fontSize="10px" fontWeight={500}>{t('REPORT.FILTER.BRANCH')}: {branchName}</Typography>
          <Typography fontSize="10px" fontWeight={500}>{t('REPORT.FILTER.START')}: {filter.rangeDate[0] ? toBuddhistYear(moment(filter.rangeDate[0]), 'DD/MM/YYYY') : '-'}</Typography>
          <Typography fontSize="10px" fontWeight={500}>{t('REPORT.FILTER.DATE_END')}: {filter.rangeDate[1] ? toBuddhistYear(moment(filter.rangeDate[1]), 'DD/MM/YYYY') : '-'}</Typography>
          <Typography fontSize="10px" fontWeight={500}>ประเภทผลิตภัณฑ์: {productTypeNames}</Typography>
          <Typography fontSize="10px" fontWeight={500}>รายการผลิตภัณฑ์: {productNames}</Typography>
          <Typography fontSize="10px" fontWeight={500}>ประเภทหัตถการ: {operativeTypeNames}</Typography>
          <Typography fontSize="10px" fontWeight={500}>หัตถการ: {operativeNames}</Typography>
        </Box>
        <IncomeList
          page={1}
          pageLimit={incomeExport?.length || 999999}
          rowCount={incomeExport?.length || 0}
          sortBy={filter.sortBy}
          sortType={filter.sortType}
          data={incomeExport}
          dataExport={incomeExport}
          onRequestSort={onChangeIncomeSort}
          handleChangeRowsPerPage={onChangeIncomeRowPerPage}
          handleChangePage={onChangeIncomePage}
          hidePagination
        />
        <Box className="page-break-inside" mt={2}>
          <SummaryPayment data={incomeExport} />
        </Box>
        <Box className="page-break-inside" mt={2}>
          <SummaryList data={incomeExport} />
        </Box>
        <Box className="page-break-inside" mt={2}>
          <SummaryTx data={incomeExport} />
        </Box>
        <Box className="page-break-inside" mt={2}>
          <SummaryPD data={incomeDataProduct} />
        </Box>
        <Box className="page-break-inside" mt={2}>
          <SummaryTxPD operative={incomeExport} product={incomeDataProduct} />
        </Box>
      </Box>
    </Box>
  )
}

